/**
 * StationSearchFilter Component
 *
 * A multi-select dropdown filter that fetches stations from the API as the user types.
 * Shows StationID and SerialNum for identification.
 */

import {
    Menu,
    MenuItem,
    Checkbox,
    ClickAwayListener,
    Box,
    Typography,
    CircularProgress,
    Chip,
    Stack,
    Popover,
    alpha,
    useMediaQuery,
    useTheme,
    Drawer,
    IconButton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useMemo, useState, useCallback, useRef } from 'react';
import { SearchBar } from './SearchBar';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';
import { useStationSearchQuery } from '@features/redux/apis/system';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import type { StationSearchResult } from '@api/system';

const DEBOUNCE_MS = 300;

interface SelectedStation {
    stationId: string;
    label: string;
}

interface StationSearchFilterProps {
    label: string;
    value: Record<string, boolean>;
    selectedStations: SelectedStation[];
    onChange: (station: SelectedStation) => void;
    onRemove: (stationId: string) => void;
}

export const StationSearchFilter = ({
    label,
    value,
    selectedStations,
    onChange,
    onRemove,
}: StationSearchFilterProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);
    const [searchText, setSearchText] = useState('');
    const [menuWidth, setMenuWidth] = useState(280);
    const [chipAnchor, setChipAnchor] = useState<HTMLElement | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    
    // Fixed height for mobile drawer
    const FIXED_DRAWER_HEIGHT = '60vh';

    const debouncedSearch = useDebouncedValue(searchText, DEBOUNCE_MS);

    const { data: searchResults = [], isFetching } = useStationSearchQuery(
        { search: debouncedSearch },
        { skip: debouncedSearch.length < 2 }
    );

    const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
        const anchorWidth = e.currentTarget.getBoundingClientRect().width;
        setMenuWidth(Math.max(280, anchorWidth));
        setDropdownAnchor(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setDropdownAnchor(null);
        setSearchText('');
    }, []);

    const handleSelect = useCallback(
        (station: StationSearchResult) => {
            const stationId = String(station.StationID);
            const label = `#${station.StationID} - ${station.SerialNum}`;
            onChange({ stationId, label });
        },
        [onChange]
    );

    // Pluralize label properly (handles irregular plurals)
    const pluralizeLabel = (text: string) => {
        if (
            text.endsWith('s') ||
            text.endsWith('x') ||
            text.endsWith('z') ||
            text.endsWith('ch') ||
            text.endsWith('sh')
        ) {
            return `${text}es`;
        }
        // Words ending in consonant + y → replace y with ies (Company → Companies)
        const vowels = ['a', 'e', 'i', 'o', 'u'];
        if (text.endsWith('y') && text.length > 1 && !vowels.includes(text[text.length - 2].toLowerCase())) {
            return `${text.slice(0, -1)}ies`;
        }
        return `${text}s`;
    };

    const getDisplayText = () => {
        if (selectedStations.length === 0) {
            return `All ${pluralizeLabel(label)}`;
        }
        if (selectedStations.length === 1) {
            return selectedStations[0].label;
        }
        return `${selectedStations.length} selected`;
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            {/* Label */}
            <Typography
                component='label'
                sx={{
                    display: 'block',
                    fontSize: typographyTokens.fontSize.sm,
                    fontWeight: typographyTokens.fontWeight.medium,
                    color: colors.neutral[500],
                    mb: '6px',
                }}>
                {label}
            </Typography>

            {/* Select Trigger */}
            <Box
                onClick={handleOpen}
                // Only enable hover interactions on desktop
                onMouseEnter={isMobile ? undefined : (e) => selectedStations.length > 0 && setChipAnchor(e.currentTarget)}
                onMouseLeave={isMobile ? undefined : (e) => {
                    const related = e.relatedTarget as HTMLElement;
                    if (!related?.closest('.chip-hover-bridge, .chip-popover')) setChipAnchor(null);
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    px: 1.5,
                    py: 1,
                    borderRadius: `${radii.md}px`,
                    border: `1px solid ${dropdownAnchor ? colors.primary[500] : colors.neutral[300]}`,
                    backgroundColor: colors.neutral[0],
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                    '&:hover': {
                        borderColor: colors.neutral[400],
                    },
                }}>
                <Typography
                    noWrap
                    sx={{
                        fontSize: typographyTokens.fontSize.sm,
                        color: selectedStations.length > 0 ? colors.neutral[800] : colors.neutral[500],
                        flex: 1,
                    }}>
                    {getDisplayText()}
                </Typography>
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 20,
                        color: colors.neutral[400],
                        transform: dropdownAnchor ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms ease',
                    }}
                />
            </Box>

            {/* Chip Popover on Hover - Desktop only */}
            {!isMobile && Boolean(chipAnchor) && (
                <Box
                    className='chip-hover-bridge'
                    sx={{
                        position: 'absolute',
                        bottom: -8,
                        left: 0,
                        right: 0,
                        height: 8,
                        zIndex: 1300,
                    }}
                    onMouseEnter={() => chipAnchor && setChipAnchor(chipAnchor)}
                />
            )}

            {!isMobile && selectedStations.length > 0 && (
                <Popover
                    open={Boolean(chipAnchor)}
                    anchorEl={chipAnchor}
                    sx={{
                        pointerEvents: 'none',
                        '& .MuiPaper-root': {
                            pointerEvents: 'auto',
                        },
                    }}
                    onClose={() => setChipAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        className: 'chip-popover',
                        onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
                            const related = e.relatedTarget as HTMLElement;
                            if (!related?.closest('.chip-hover-bridge, .MuiButton-root')) setChipAnchor(null);
                        },
                        sx: {
                            pointerEvents: 'auto',
                            mt: 0.5,
                            p: 1.5,
                            maxWidth: 300,
                            maxHeight: 200,
                            overflow: 'auto',
                            borderRadius: `${radii.md}px`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        },
                    }}>
                    <Stack spacing={0.75}>
                        {selectedStations.map((station) => (
                            <Chip
                                key={`chip-${station.stationId}`}
                                label={station.label}
                                size='small'
                                onDelete={() => onRemove(station.stationId)}
                                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    backgroundColor: alpha(colors.primary[500], 0.1),
                                    color: colors.primary[700],
                                    borderRadius: `${radii.sm}px`,
                                    fontSize: typographyTokens.fontSize.xs,
                                    height: 26,
                                    '& .MuiChip-deleteIcon': {
                                        color: colors.primary[500],
                                        '&:hover': {
                                            color: colors.primary[700],
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                </Popover>
            )}

            {/* Mobile: Bottom Drawer */}
            {isMobile ? (
                <Drawer
                    anchor='bottom'
                    open={Boolean(dropdownAnchor)}
                    onClose={handleClose}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            height: FIXED_DRAWER_HEIGHT,
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    }}>
                    {/* Drawer Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.md,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[800],
                            }}>
                            Search {label}
                        </Typography>
                        <IconButton
                            size='small'
                            onClick={handleClose}
                            sx={{ color: colors.neutral[500] }}>
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Box>

                    {/* Search */}
                    <Box sx={{ p: 2, borderBottom: `1px solid ${colors.neutral[200]}`, '& > div': { width: '100%' } }}>
                        <SearchBar value={searchText} onSearch={setSearchText} />
                    </Box>

                    {/* Results */}
                    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                        {/* Show selected stations so they can be deselected */}
                        {selectedStations.length > 0 && (
                            <Box sx={{ borderBottom: `1px solid ${colors.neutral[200]}` }}>
                                <Typography
                                    sx={{
                                        px: 2,
                                        pt: 1.5,
                                        pb: 0.5,
                                        fontSize: typographyTokens.fontSize.xs,
                                        fontWeight: typographyTokens.fontWeight.semibold,
                                        color: colors.neutral[500],
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}>
                                    Selected ({selectedStations.length})
                                </Typography>
                                {selectedStations.map((station) => (
                                    <Box
                                        key={`selected-${station.stationId}`}
                                        onClick={() => onRemove(station.stationId)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 2,
                                            py: 1.5,
                                            borderBottom: `1px solid ${colors.neutral[100]}`,
                                            cursor: 'pointer',
                                            backgroundColor: colors.primary[50],
                                            '&:active': {
                                                backgroundColor: colors.neutral[100],
                                            },
                                        }}>
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.sm,
                                                fontWeight: 500,
                                                color: colors.primary[700],
                                            }}>
                                            {station.label}
                                        </Typography>
                                        <CloseIcon sx={{ fontSize: 18, color: colors.neutral[400] }} />
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {searchText.length < 2 ? (
                            selectedStations.length === 0 ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 6,
                                        color: colors.neutral[500],
                                        fontSize: typographyTokens.fontSize.sm,
                                    }}>
                                    Type at least 2 characters to search
                                </Box>
                            ) : null
                        ) : isFetching ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} sx={{ color: colors.primary[500] }} />
                            </Box>
                        ) : searchResults.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 6,
                                    color: colors.neutral[500],
                                    fontSize: typographyTokens.fontSize.sm,
                                }}>
                                No systems found
                            </Box>
                        ) : (
                            searchResults.map((station) => {
                                const stationId = String(station.StationID);
                                const isSelected = Boolean(value[stationId]);
                                return (
                                    <Box
                                        key={station.StationID}
                                        onClick={() => handleSelect(station)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 2,
                                            py: 1.5,
                                            borderBottom: `1px solid ${colors.neutral[100]}`,
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? colors.primary[50] : 'transparent',
                                            '&:active': {
                                                backgroundColor: colors.neutral[100],
                                            },
                                        }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography
                                                sx={{
                                                    fontSize: typographyTokens.fontSize.sm,
                                                    fontWeight: isSelected ? 500 : 400,
                                                    color: isSelected ? colors.primary[700] : colors.neutral[800],
                                                }}>
                                                #{station.StationID}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: typographyTokens.fontSize.xs,
                                                    color: colors.neutral[500],
                                                }}>
                                                {station.SerialNum}
                                            </Typography>
                                        </Box>
                                        {isSelected && (
                                            <CheckIcon sx={{ fontSize: 20, color: colors.primary[500] }} />
                                        )}
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Drawer>
            ) : (
                /* Desktop: Regular Menu */
            <Menu
                open={Boolean(dropdownAnchor)}
                anchorEl={dropdownAnchor}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        width: menuWidth,
                        maxHeight: 400,
                        mt: 0.5,
                        borderRadius: `${radii.md}px`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    },
                }}
                MenuListProps={{ style: { padding: 0 }, ref: menuRef as any }}>
                <ClickAwayListener onClickAway={handleClose}>
                    <Box sx={{ width: '100%' }}>
                        {/* Search Input */}
                        <Box
                            p={1.5}
                            sx={{
                                position: 'sticky',
                                top: 0,
                                bgcolor: 'background.paper',
                                zIndex: 1,
                                borderBottom: `1px solid ${colors.neutral[200]}`,
                            }}>
                            <SearchBar width={menuWidth - 24} value={searchText} onSearch={setSearchText} />
                        </Box>

                            {/* Selected stations */}
                            {selectedStations.length > 0 && (
                                <Box sx={{ borderBottom: `1px solid ${colors.neutral[200]}`, p: 1 }}>
                                    <Typography
                                        sx={{
                                            px: 1,
                                            py: 0.5,
                                            fontSize: typographyTokens.fontSize.xs,
                                            fontWeight: typographyTokens.fontWeight.semibold,
                                            color: colors.neutral[500],
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}>
                                        Selected ({selectedStations.length})
                                    </Typography>
                                    {selectedStations.map((station) => (
                                        <MenuItem
                                            key={`selected-${station.stationId}`}
                                            onClick={() => onRemove(station.stationId)}
                                            sx={{
                                                borderRadius: `${radii.sm}px`,
                                                mb: 0.5,
                                                backgroundColor: colors.primary[50],
                                                '&:hover': {
                                                    backgroundColor: colors.primary[100],
                                                },
                                            }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: typographyTokens.fontSize.sm,
                                                        fontWeight: 500,
                                                        color: colors.primary[700],
                                                    }}>
                                                    {station.label}
                                                </Typography>
                                                <CloseIcon sx={{ fontSize: 16, color: colors.neutral[400] }} />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Box>
                            )}

                        {/* Results */}
                        <Box sx={{ p: 1 }}>
                            {searchText.length < 2 ? (
                                    selectedStations.length === 0 ? (
                                <Typography
                                    sx={{
                                        p: 2,
                                        textAlign: 'center',
                                        color: colors.neutral[500],
                                        fontSize: typographyTokens.fontSize.sm,
                                    }}>
                                    Type at least 2 characters to search
                                </Typography>
                                    ) : null
                            ) : isFetching ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress size={24} sx={{ color: colors.primary[500] }} />
                                </Box>
                            ) : searchResults.length === 0 ? (
                                <Typography
                                    sx={{
                                        p: 2,
                                        textAlign: 'center',
                                        color: colors.neutral[500],
                                        fontSize: typographyTokens.fontSize.sm,
                                    }}>
                                    No systems found
                                </Typography>
                            ) : (
                                searchResults.map((station) => {
                                    const stationId = String(station.StationID);
                                    const isSelected = Boolean(value[stationId]);
                                    return (
                                        <MenuItem
                                            key={station.StationID}
                                            onClick={() => handleSelect(station)}
                                            sx={{
                                                borderRadius: `${radii.sm}px`,
                                                mb: 0.5,
                                                '&:hover': {
                                                    backgroundColor: colors.neutral[100],
                                                },
                                            }}>
                                            <Checkbox
                                                size='small'
                                                disableRipple
                                                checked={isSelected}
                                                sx={{
                                                    color: colors.neutral[400],
                                                    '&.Mui-checked': {
                                                        color: colors.primary[500],
                                                    },
                                                }}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: typographyTokens.fontSize.sm,
                                                        fontWeight: 500,
                                                        color: colors.neutral[800],
                                                    }}>
                                                    #{station.StationID}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: typographyTokens.fontSize.xs,
                                                        color: colors.neutral[500],
                                                    }}>
                                                    {station.SerialNum}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Menu>
            )}
        </Box>
    );
};
